import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Star, MapPin, ChevronRight } from 'lucide-react';
import { ProviderLevelBadge } from './ServiceProviderBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ServiceCardCompact (Grid Card D)
 * Minimal card for sidebar or dense grid layouts
 * Shows essential info: provider, rating, price
 */
const ServiceCardCompact = ({ 
  service, 
  onClick,
  className = '' 
}) => {
  const {
    id,
    provider_name,
    provider_image,
    service_name,
    rating = 0,
    review_count = 0,
    starting_price,
    currency = 'Rs',
    distance,
    verification_level = 0,
    is_sponsored = false,
  } = service;

  const imageUrl = provider_image 
    ? (provider_image.startsWith('http') ? provider_image : `${API_URL}${provider_image}`)
    : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';

  return (
    <Card 
      className={`group hover:shadow-md transition-all duration-200 overflow-hidden border hover:border-purple-300 cursor-pointer ${className}`}
      onClick={() => onClick?.(service)}
      data-testid={`service-card-compact-${id}`}
    >
      <div className="p-3 flex items-center gap-3">
        {/* Provider Image */}
        <div className="relative flex-shrink-0">
          <img
            src={imageUrl}
            alt={provider_name}
            className="w-12 h-12 rounded-full object-cover border border-gray-200"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';
            }}
          />
          {is_sponsored && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">★</span>
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900 text-sm truncate group-hover:text-purple-600">
              {provider_name}
            </h4>
            <ProviderLevelBadge level={verification_level} size="xs" />
          </div>
          <p className="text-xs text-gray-500 truncate">{service_name}</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-0.5">
              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
              <span className="text-xs font-medium">{rating.toFixed(1)}</span>
            </div>
            {distance && (
              <div className="flex items-center gap-0.5 text-xs text-gray-400">
                <MapPin className="h-3 w-3" />
                <span>{distance}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Price & Arrow */}
        <div className="flex-shrink-0 text-right">
          <p className="font-bold text-purple-600 text-sm">
            {currency} {starting_price?.toLocaleString()}
          </p>
          <ChevronRight className="h-4 w-4 text-gray-400 ml-auto group-hover:text-purple-600 transition-colors" />
        </div>
      </div>
    </Card>
  );
};

export default ServiceCardCompact;
